import React, { useState, useEffect, useRef } from 'react';
import { technologies, testimonial } from '../../data/mock';
import { Star, Quote } from 'lucide-react';

const techTabs = [
  { id: 'sap', label: 'SAP' },
  { id: 'web', label: 'Web' },
  { id: 'databases', label: 'Databases' },
  { id: 'cloud', label: 'Cloud' },
  { id: 'mobile', label: 'Mobile' },
];

const TechnologiesSection = () => {
  const [activeTab, setActiveTab] = useState('sap');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gray-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Technologies */}
          <div className="reveal">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-[#ff6b35] text-sm font-semibold rounded-full mb-4">
              Technologies
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Tech Stack We Master
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              We leverage the latest technologies to build scalable, secure, and high-performance solutions.
            </p>

            {/* Tech Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {techTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#ff6b35] text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tech Pills */}
            <div className="flex flex-wrap gap-3">
              {technologies[activeTab]?.map((tech, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm hover:bg-white/10 hover:border-[#ff6b35]/50 transition-all cursor-default"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Right - Testimonial */}
          <div className="reveal-scale delay-200">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <Quote className="w-12 h-12 text-[#ff6b35] mb-6 opacity-50" />
              
              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                "{testimonial.content}"
              </p>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] flex items-center justify-center">
                  <span className="text-white font-bold text-xl">{testimonial.author.charAt(0)}</span>
                </div>
                <div>
                  <div className="font-semibold text-white">{testimonial.author}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                  <div className="text-[#ff6b35] text-sm">{testimonial.location}</div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-4 mt-8 pt-8 border-t border-white/10">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-400 text-sm">Based on {testimonial.reviews.google}+ reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologiesSection;
